// Custom hook for Firebase Authentication
import { useState, useEffect, useRef } from 'react';
import {
  signInWithPopup,
  signInWithCredential,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { getAuthInstance, getGoogleProvider, getFirestoreInstance, initializationPromise } from './firebase';
import { profileFieldsFromFirestoreDoc } from './userProfile';

// Helper function to push user data to GTM dataLayer
// Only pushes if user state actually changed to prevent duplicates
const pushUserToDataLayer = (user) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    const currentUserId = user?.uid || null;

    // Only push if user state changed
    if (currentUserId === lastPushedUserId) {
      return; // Skip duplicate push
    }

    lastPushedUserId = currentUserId;

    if (user && user.uid && user.email) {
      // Extract email part before @ for user_metadata
      const emailPrefix = user.email.split('@')[0];

      window.dataLayer.push({
        user_id: user.uid,
        user_metadata: emailPrefix,
        event: 'user_logged_in',
      });
    }
  }
};

// Module-level singleton to ensure only one auth listener exists
// This prevents duplicate calls even with React Strict Mode
let globalAuthListener = null;
let globalAuthSubscribers = new Set();
let globalAuthState = {
  user: null,
  idToken: null,
  isAuthenticated: false,
  loading: true,
  userProfileReady: false,
};
// Global flags to prevent duplicate verification across all component instances
let globalHasVerifiedInitialLoad = false;
let globalIsVerifying = false;
// Track last user ID pushed to dataLayer to prevent duplicate pushes
let lastPushedUserId = null;
// One Tap singleton flags (module scope to avoid duplicate init in Strict Mode)
let oneTapInitialized = false;
let oneTapInitializing = false;
let oneTapLastPromptAt = 0;
// Firestore user profile listener (singleton)
let globalUserProfileUnsubscribe = null;
let globalListeningUid = null;

const ONE_TAP_PROMPT_COOLDOWN_MS = 15000;

const getGoogleOneTapClientId = () => {
  return process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
};

const waitForGoogleIdentityServices = async (maxAttempts = 50, delayMs = 100) => {
  let attempts = 0;
  while (attempts < maxAttempts) {
    if (window.google?.accounts?.id) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    attempts += 1;
  }
  return false;
};

const initializeGoogleOneTap = async () => {
  if (typeof window === 'undefined') return false;
  if (oneTapInitialized || oneTapInitializing) return oneTapInitialized;

  const clientId = getGoogleOneTapClientId();
  if (!clientId) return false;

  oneTapInitializing = true;
  try {
    const gisReady = await waitForGoogleIdentityServices();
    if (!gisReady) return false;

    window.google.accounts.id.initialize({
      client_id: clientId,
      auto_select: true,
      use_fedcm_for_prompt: true,
      cancel_on_tap_outside: false,
      callback: async (response) => {
        try {
          if (!response?.credential) return;
          const authInstance = getAuthInstance();
          const credential = GoogleAuthProvider.credential(response.credential);
          await signInWithCredential(authInstance, credential);
        } catch (_) {
          // One Tap failures should not break normal auth flows.
        }
      },
    });

    oneTapInitialized = true;
    return true;
  } finally {
    oneTapInitializing = false;
  }
};

const maybePromptGoogleOneTap = () => {
  if (typeof window === 'undefined') return;
  if (!oneTapInitialized || !window.google?.accounts?.id) return;
  if (globalAuthState.isAuthenticated || globalIsVerifying) return;

  const now = Date.now();
  if (now - oneTapLastPromptAt < ONE_TAP_PROMPT_COOLDOWN_MS) {
    return;
  }
  oneTapLastPromptAt = now;
  window.google.accounts.id.prompt((notification) => {
    if (process.env.NODE_ENV !== 'development') return;
    try {
      const status = {
        display: notification?.isDisplayMoment?.() || false,
        notDisplayed: notification?.isNotDisplayed?.() || false,
        skipped: notification?.isSkippedMoment?.() || false,
        dismissed: notification?.isDismissedMoment?.() || false,
      };
      const reason =
        notification?.getNotDisplayedReason?.() ||
        notification?.getSkippedReason?.() ||
        notification?.getDismissedReason?.() ||
        null;
      if (reason || status.notDisplayed || status.skipped) {
        console.info('[OneTap] prompt status', status, reason ? { reason } : {});
      }
    } catch (_) {
      // Keep One Tap non-blocking even if logger callback throws.
    }
  });
};

const stopUserProfileListener = () => {
  if (globalUserProfileUnsubscribe) {
    globalUserProfileUnsubscribe();
    globalUserProfileUnsubscribe = null;
  }
  globalListeningUid = null;
  globalAuthState.userProfileReady = false;
};

const mergeFirestoreIntoGlobalUser = (firestoreData) => {
  const current = globalAuthState.user;
  if (!current?.uid) return;

  const profile = profileFieldsFromFirestoreDoc(firestoreData);
  const prevTimeSaved = current.time_saved_seconds;
  let timeSaved = profile.time_saved_seconds ?? 0;
  // Preserve optimistic bump from updateUserTimeSaved until Firestore catches up.
  if (prevTimeSaved != null && prevTimeSaved > timeSaved) {
    timeSaved = prevTimeSaved;
  }

  globalAuthState.user = {
    ...current,
    ...profile,
    time_saved_seconds: timeSaved,
    displayName: current.displayName,
    email: current.email,
    photoURL: current.photoURL,
    uid: current.uid,
  };
  notifySubscribers();
};

const startUserProfileListener = async (uid) => {
  if (!uid) return;
  if (globalListeningUid === uid && globalUserProfileUnsubscribe) return;

  stopUserProfileListener();
  globalListeningUid = uid;
  globalAuthState.userProfileReady = false;
  notifySubscribers();

  try {
    await initializationPromise;
    const db = getFirestoreInstance();
    const userRef = doc(db, 'users', uid);

    globalUserProfileUnsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        globalAuthState.userProfileReady = true;
        mergeFirestoreIntoGlobalUser(snapshot.exists() ? snapshot.data() : null);
      },
      (error) => {
        console.error('User profile snapshot error:', error);
        globalAuthState.userProfileReady = true;
        notifySubscribers();
      }
    );
  } catch (error) {
    console.error('Failed to start user profile listener:', error);
    globalAuthState.userProfileReady = true;
    notifySubscribers();
  }
};

// Initialize global auth listener (called once when module loads)
// Uses singleton pattern to ensure only one listener exists, preventing duplicate calls
const initializeGlobalAuthListener = async () => {
  if (globalAuthListener) {
    return; // Already initialized
  }

  // Wait for Firebase to be ready
  let attempts = 0;
  const maxAttempts = 50;

  while (attempts < maxAttempts) {
    try {
      const authInstance = getAuthInstance();
      if (authInstance) {
        break;
      }
    } catch (error) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts += 1;
    }
  }

  try {
    const authInstance = getAuthInstance();
    let tokenRefreshInterval = null;

    globalAuthListener = onAuthStateChanged(authInstance, async (firebaseUser) => {
      // Clear any existing token refresh interval
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
      }

      if (firebaseUser) {
        // Function to refresh token
        const refreshToken = async () => {
          try {
            const token = await firebaseUser.getIdToken(true);
            globalAuthState.idToken = token;
            notifySubscribers();
          } catch (error) {
            // Error refreshing token - silently fail
          }
        };

        // Get initial token
        const token = await firebaseUser.getIdToken();
        globalAuthState.user = firebaseUser;
        globalAuthState.idToken = token;
        globalAuthState.isAuthenticated = true;
        globalAuthState.loading = false;

        // Set up automatic token refresh every 50 minutes
        tokenRefreshInterval = setInterval(refreshToken, 50 * 60 * 1000);

        // Push to dataLayer once (not per subscriber)
        pushUserToDataLayer(firebaseUser);

        // Live profile from Firestore (premium, phone, streak, etc.)
        startUserProfileListener(firebaseUser.uid);

        // Notify all subscribers
        notifySubscribers();
      } else {
        stopUserProfileListener();

        // User logged out
        globalAuthState.user = null;
        globalAuthState.idToken = null;
        globalAuthState.isAuthenticated = false;
        globalAuthState.loading = false;
        globalAuthState.userProfileReady = false;

        // Push to dataLayer once (not per subscriber)
        pushUserToDataLayer(null);

        // Notify all subscribers
        notifySubscribers();
      }
    });
  } catch (error) {
    globalAuthState.loading = false;
    notifySubscribers();
  }
};

// Notify all subscribers of auth state changes
const notifySubscribers = () => {
  globalAuthSubscribers.forEach((callback) => {
    callback({
      user: globalAuthState.user,
      idToken: globalAuthState.idToken,
      isAuthenticated: globalAuthState.isAuthenticated,
      loading: globalAuthState.loading,
      userProfileReady: globalAuthState.userProfileReady,
    });
  });
};

// Export function to update user time_saved_seconds from App.js
export const updateUserTimeSaved = (newTimeSavedSeconds) => {
  if (globalAuthState.user) {
    globalAuthState.user = {
      ...globalAuthState.user,
      time_saved_seconds: newTimeSavedSeconds,
    };
    notifySubscribers();
  }
};

/** Update push reminder fields on global user. */
export const updateUserPush = (fields) => {
  if (globalAuthState.user) {
    globalAuthState.user = {
      ...globalAuthState.user,
      ...fields,
    };
    notifySubscribers();
  }
};

/** Update creator streak fields on global user (after check-in). */
export const updateUserStreak = (streakCount, streakLastDate) => {
  if (globalAuthState.user) {
    globalAuthState.user = {
      ...globalAuthState.user,
      creator_streak_count: Math.max(1, streakCount || 1),
      creator_streak_last_date: streakLastDate || globalAuthState.user.creator_streak_last_date,
    };
    notifySubscribers();
  }
};

/** Core /api/auth/verify call — bootstrap user doc; profile fields come from Firestore snapshot. */
const executeVerifyTokenWithBackend = async (token, firebaseUser) => {
  globalIsVerifying = true;
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    let landingUrl = null;
    try {
      landingUrl = sessionStorage.getItem('referral_source_url') || null;
    } catch (_) {}

    const response = await fetch('/api/auth/verify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id_token: token, referral_source_url: landingUrl }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      if (data.token_valid) {
        // Merge verify response; snapshot keeps fields live after bootstrap.
        const userData = {
          ...globalAuthState.user,
          ...firebaseUser,
          ...data.user,
        };
        globalAuthState.user = userData;
        globalAuthState.isAuthenticated = true;
        notifySubscribers();
      }
    } else {
      globalAuthState.user = { ...globalAuthState.user, ...firebaseUser };
      globalAuthState.isAuthenticated = true;
      notifySubscribers();
    }
  } catch (error) {
    globalAuthState.user = { ...globalAuthState.user, ...firebaseUser };
    globalAuthState.isAuthenticated = true;
    notifySubscribers();
  } finally {
    globalIsVerifying = false;
  }
};

/**
 * Wait for premium in Firestore snapshot, with verify fallback for edge cases.
 */
export const refreshUserFromBackend = async () => {
  try {
    const authInstance = getAuthInstance();
    const cu = authInstance?.currentUser;
    if (!cu) {
      return { ok: false, premium: false };
    }

    if (globalAuthState.user?.plan_tier === 'premium') {
      return { ok: true, premium: true };
    }

    const deadline = Date.now() + 4000;
    while (Date.now() < deadline) {
      if (globalAuthState.user?.plan_tier === 'premium') {
        return { ok: true, premium: true };
      }
      await new Promise((r) => setTimeout(r, 150));
    }

    let spins = 0;
    while (globalIsVerifying && spins < 200) {
      await new Promise((r) => setTimeout(r, 50));
      spins += 1;
    }

    const token = await cu.getIdToken(true);
    globalAuthState.idToken = token;
    notifySubscribers();

    await executeVerifyTokenWithBackend(token, cu);

    const premium = globalAuthState.user?.plan_tier === 'premium';
    return { ok: true, premium };
  } catch (_) {
    return { ok: false, premium: false };
  }
};

export const useAuth = () => {
  const [user, setUser] = useState(globalAuthState.user);
  const [loading, setLoading] = useState(globalAuthState.loading);
  const [idToken, setIdToken] = useState(globalAuthState.idToken);
  const [isAuthenticated, setIsAuthenticated] = useState(globalAuthState.isAuthenticated);
  const [userProfileReady, setUserProfileReady] = useState(globalAuthState.userProfileReady);
  const subscriberRef = useRef(null);

  // Skips if another verify is already running (initial load / concurrent)
  const verifyTokenWithBackend = async (token, firebaseUser) => {
    if (globalIsVerifying) {
      return;
    }
    await executeVerifyTokenWithBackend(token, firebaseUser);
  };

  // Initialize global auth listener once (module-level singleton)
  useEffect(() => {
    initializeGlobalAuthListener();
  }, []);

  // Initialize and prompt Google One Tap (uses same Firebase auth state flow)
  useEffect(() => {
    let isCancelled = false;

    const setupOneTap = async () => {
      const initialized = await initializeGoogleOneTap();
      if (!initialized || isCancelled) return;

      if (globalAuthState.isAuthenticated) {
        window.google?.accounts?.id?.cancel?.();
        return;
      }
      maybePromptGoogleOneTap();
    };

    setupOneTap();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated]);

  // Retry One Tap in common return paths (focus/tab-visible/first interaction).
  useEffect(() => {
    if (isAuthenticated) return;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        maybePromptGoogleOneTap();
      }
    };

    const onFocus = () => {
      maybePromptGoogleOneTap();
    };

    const onFirstInteraction = () => {
      maybePromptGoogleOneTap();
      window.removeEventListener('pointerdown', onFirstInteraction, true);
      window.removeEventListener('keydown', onFirstInteraction, true);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pointerdown', onFirstInteraction, true);
    window.addEventListener('keydown', onFirstInteraction, true);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pointerdown', onFirstInteraction, true);
      window.removeEventListener('keydown', onFirstInteraction, true);
    };
  }, [isAuthenticated]);

  // Subscribe to global auth state changes
  useEffect(() => {
    const subscriber = (state) => {
      setUser(state.user);
      setLoading(state.loading);
      setIdToken(state.idToken);
      setIsAuthenticated(state.isAuthenticated);
      setUserProfileReady(state.userProfileReady);

      // Bootstrap once per session: create user doc, referral attribution, etc.
      if (state.user && !globalHasVerifiedInitialLoad && !globalIsVerifying) {
        globalHasVerifiedInitialLoad = true;
        verifyTokenWithBackend(state.idToken, state.user);
      } else if (!state.user) {
        globalHasVerifiedInitialLoad = false;
      }
    };

    subscriberRef.current = subscriber;
    globalAuthSubscribers.add(subscriber);

    subscriber(globalAuthState);

    return () => {
      globalAuthSubscribers.delete(subscriber);
      subscriberRef.current = null;
    };
  }, []);

  const signInWithGoogle = async () => {
    setLoading(true);

    try {
      const authInstance = getAuthInstance();
      const provider = getGoogleProvider();
      const result = await signInWithPopup(authInstance, provider);
      const token = await result.user.getIdToken();

      globalHasVerifiedInitialLoad = true;

      await verifyTokenWithBackend(token, result.user);

      setLoading(false);

      return { success: true, user: result.user };
    } catch (error) {
      setLoading(false);
      globalHasVerifiedInitialLoad = false;
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      const authInstance = getAuthInstance();
      await signOut(authInstance);
      setUser(null);
      setIdToken(null);
      setIsAuthenticated(false);
      setUserProfileReady(false);
      globalHasVerifiedInitialLoad = false;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getFreshToken = async () => {
    if (!user) return null;
    try {
      const authInstance = getAuthInstance();
      const currentUser = authInstance.currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken();
        setIdToken(token);
        return token;
      }
      return idToken;
    } catch (error) {
      return idToken;
    }
  };

  const triggerGoogleOneTap = async () => {
    try {
      const initialized = await initializeGoogleOneTap();
      if (!initialized) return false;
      maybePromptGoogleOneTap();
      return true;
    } catch (_) {
      return false;
    }
  };

  return {
    user,
    loading,
    idToken,
    isAuthenticated,
    userProfileReady,
    signInWithGoogle,
    triggerGoogleOneTap,
    logout,
    getFreshToken,
  };
};
